import React from "react";

import { Typography } from "@packages/ds";
import momentjs from "moment";
import { useIntl } from "react-intl";

import * as Table from "ds/Table";
import { formatAsCurrency } from "Libs/utils";
import { formattedOrderProjectTotal } from "src/organization/pages/billing/util";

import StandardUserMgmtMsg from "../StandardUserMgmtMsg";

import * as S from "./ProjectDetail.styles";

import type { OrderLineItem, LineItemComponent } from "@packages/client";
import type { OrderLineItemsNormalized } from "src/organization/pages/billing/util";

type OrderProjectDetailProps = {
  subscriptionId: string;
  lineItemsByOrder?: OrderLineItemsNormalized;
  items?: OrderLineItem[];
  totals?: Record<string, LineItemComponent>;
};

const OrderProjectDetail = ({
  subscriptionId,
  lineItemsByOrder,
  items,
  totals
}: OrderProjectDetailProps) => {
  const intl = useIntl();

  const lineItems = items ?? lineItemsByOrder?.[subscriptionId].line_items;
  const durationDate = date => momentjs(date).format("MMM D");
  const orderCurrency = lineItems?.[0].components.base_price.currency;
  const isStandardUserManagementLine = lineItem =>
    typeof lineItem.components.standard_user_management_discount !==
    "undefined";

  let orderDetailItemTotal: React.ReactNode = null;
  if (lineItems?.length) {
    // Assume the currency is the same for all components of the line item.
    const currency = lineItems[0].components.base_price.currency;

    const { totalList, formattedTotal } = formattedOrderProjectTotal({
      totals,
      lineItems,
      currency
    });
    orderDetailItemTotal = (
      <div>
        {totalList &&
          Object.keys(totalList)
            .filter(item => item !== "totalPrice")
            .map(item => (
              <React.Fragment key={totalList[item]?.display_title}>
                <S.Hr />
                <S.OrderDetailTableTr>
                  <Table.Td width="">
                    <Typography.BodyText $color="ebony" weight="semiBold">
                      {totalList[item]?.display_title}
                    </Typography.BodyText>
                  </Table.Td>
                  <S.RightTableTd width="">
                    <Typography.BodyText $color="ebony" weight="bold">
                      {formatAsCurrency(totalList[item]?.amount, currency)}
                    </Typography.BodyText>
                  </S.RightTableTd>
                </S.OrderDetailTableTr>
              </React.Fragment>
            ))}

        <S.TotalTableTr>
          <Table.Td width="">
            <Typography.BodyText weight="bold">
              {intl.formatMessage({
                id: "project_detail.total",
                defaultMessage: "Total"
              })}
            </Typography.BodyText>
          </Table.Td>
          <Table.Td width="">
            <Typography.BodyText $color="ebony" weight="bold">
              {formattedTotal}
            </Typography.BodyText>
          </Table.Td>
        </S.TotalTableTr>
      </div>
    );
  }

  return (
    <Table.Table>
      <S.TableThead hasBorderBottom>
        <Table.Th>
          <Typography.BodyText
            weight="semiBold"
            size="extraSmall"
            $color="granite"
          >
            {intl.formatMessage({
              id: "project_detail.header.item",
              defaultMessage: "Item"
            })}
          </Typography.BodyText>
        </Table.Th>
        <Table.Th>
          <Typography.BodyText
            weight="semiBold"
            size="extraSmall"
            $color="granite"
          >
            {intl.formatMessage({
              id: "project_detail.header.duration",
              defaultMessage: "Duration"
            })}
          </Typography.BodyText>
        </Table.Th>
        <Table.Th>
          <Typography.BodyText
            weight="semiBold"
            size="extraSmall"
            $color="granite"
          >
            {intl.formatMessage({
              id: "project_detail.header.unit_price",
              defaultMessage: "Unit price"
            })}
          </Typography.BodyText>
        </Table.Th>
        <Table.Th>
          <Typography.BodyText
            weight="semiBold"
            size="extraSmall"
            $color="granite"
          >
            {intl.formatMessage({
              id: "project_detail.header.quantity",
              defaultMessage: "Quantity"
            })}
          </Typography.BodyText>
        </Table.Th>
        <S.RightTableTh>
          <Typography.BodyText
            weight="semiBold"
            size="extraSmall"
            $color="granite"
          >
            {intl.formatMessage({
              id: "project_detail.header.total",
              defaultMessage: "Total"
            })}
          </Typography.BodyText>
        </S.RightTableTh>
      </S.TableThead>
      <S.TableTbody>
        <div>
          {lineItems?.map(lineItem => (
            <React.Fragment key={lineItem.id}>
              <S.TableTr>
                <Table.Td width="">
                  <S.ProductName $color="ebony">
                    {lineItem.product}
                    {isStandardUserManagementLine(lineItem) && (
                      <StandardUserMgmtMsg />
                    )}
                  </S.ProductName>
                </Table.Td>

                <Table.Td width="">
                  <Typography.BodyText $color="ebony">
                    {`${durationDate(lineItem.start)} – ${durationDate(
                      lineItem.end
                    )}`}
                  </Typography.BodyText>
                </Table.Td>

                <Table.Td width="">
                  <Typography.BodyText $color="ebony" data-testid="unit-price">
                    {isStandardUserManagementLine(lineItem) ? (
                      <s>
                        {formatAsCurrency(lineItem.unit_price, orderCurrency)}
                      </s>
                    ) : (
                      formatAsCurrency(lineItem.unit_price, orderCurrency)
                    )}
                  </Typography.BodyText>
                  <Typography.BodyText $color="ebony">
                    {lineItem.components.discount &&
                      formatAsCurrency(
                        lineItem.components.discount?.amount,
                        orderCurrency
                      )}
                  </Typography.BodyText>
                </Table.Td>

                <Table.Td width="">
                  <Typography.BodyText>
                    {parseFloat(`${lineItem.quantity}`).toFixed(2)}
                  </Typography.BodyText>
                </Table.Td>

                <Table.Td width="">
                  <Typography.BodyText weight="bold" $color="ebony">
                    {formatAsCurrency(
                      lineItem.components.base_price?.amount,
                      orderCurrency
                    )}
                  </Typography.BodyText>
                </Table.Td>
              </S.TableTr>
            </React.Fragment>
          ))}
        </div>
        {orderDetailItemTotal}
      </S.TableTbody>
    </Table.Table>
  );
};

export default OrderProjectDetail;
